import React, { useContext, useEffect, useMemo, useState } from 'react';
import { App04AIChat, App04AIChatUser, EntityValidations, IApp04AIChat, ISpecificEntityFormProps } from '../../types';
import { LookupService } from '../../services';
import styles from '../../styles/cr.module.scss';
import { CrUserPicker } from '../cr/CrUserPicker';
import { CrTextField } from '../cr/CrTextField';
import { CrDropdown } from '../cr/CrDropdown';
import { EntityForm } from '../EntityForm';
import { DataContext } from '../DataContext';
import { IntegrationContext } from '../IntegrationContext';
import { OrbUserContext } from '../OrbUserContext';

class AiChatFormValidations extends EntityValidations {
    public TopicID: string = null;
    public Owners: string = null;
}

interface AiChatFormProps extends ISpecificEntityFormProps {

}

export const AiChatForm = (props: AiChatFormProps): React.ReactElement => {
    const { disableGroupManagement } = useContext(IntegrationContext);
    const { userContext } = useContext(OrbUserContext);
    const currentUserId: number = userContext.UserId;
    const { dataServices, lookupData, loadLookupData: { app04Topics, entityStatuses, users: { all: allUsers } } } = useContext(DataContext);

    useMemo(() => app04Topics(), [app04Topics]);
    useMemo(() => entityStatuses(), [entityStatuses]);
    useMemo(() => allUsers(), [allUsers]);

    const validateEntity = (aiChat: IApp04AIChat): Promise<AiChatFormValidations> => {
        const errors = new AiChatFormValidations();

        if (aiChat.Title === null || aiChat.Title === '') {
            errors.Title = `Chat title is required`;
            errors.Valid = false;
        }
        else {
            errors.Title = null;
        }

        if (aiChat.TopicID === null || isNaN(aiChat.TopicID)) {
            errors.TopicID = `Topic is required`;
            errors.Valid = false;
        }
        else {
            errors.TopicID = null;
        }

        if (aiChat.App04AIChatUsers === undefined || aiChat.App04AIChatUsers === null || aiChat.App04AIChatUsers.length < 1) {
            errors.Owners = `At least one owner is required`;
            errors.Valid = false;
        }
        else {
            errors.Owners = null;
        }

        return Promise.resolve(errors);
    };

    return (
        <EntityForm<IApp04AIChat, AiChatFormValidations>
            {...props}
            entityName='Cloe AI Chat'
            renderFormFields={(changeHandlers, formState) => {
                const { FormData: aiChat, ValidationErrors: errors } = formState;
                return (
                    <div>
                        <CrTextField
                            label="Chat Title"
                            maxLength={500}
                            className={styles.formField}
                            required={true}
                            value={aiChat.Title}
                            onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'Title')}
                            errorMessage={errors.Title}
                        />

                        {!(props.showEntityDescription1 === true) && (
                            <CrDropdown
                                label="Topic"
                                required={true}
                                className={styles.formField}
                                options={LookupService.entitiesToSelectableOptions(
                                    aiChat.AppID != null
                                        ? lookupData.App04Topics?.filter(t => t.AppID === aiChat.AppID)
                                        : lookupData.App04Topics
                                )}
                                selectedKey={aiChat.TopicID}
                                onChange={(_, o) => changeHandlers.changeDropdown(o, 'TopicID')}
                                errorMessage={errors.TopicID}
                            />
                        )}

                        <CrTextField
                            label="Context"
                            multiline
                            rows={6}
                            maxLength={10000}
                            className={styles.formField}
                            value={aiChat.ChatContext}
                            onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'ChatContext')}
                            placeholder="This context will be automatically included with every prompt in this chat."
                        />

                        <CrDropdown
                            label="Summary Frequency"
                            className={styles.formField}
                            options={LookupService.entitiesToSelectableOptions([
                                { ID: 10, Title: "Every 10th prompt" },
                                { ID: 20, Title: "Every 20th prompt" },
                                { ID: 30, Title: "Every 30th prompt" },
                            ])}
                            selectedKey={aiChat.SummaryFrequency}
                            onChange={(_, o) => changeHandlers.changeDropdown(o, "SummaryFrequency")}
                        />

                        <CrUserPicker
                            label="Owner(s)"
                            className={styles.formField}
                            users={lookupData.Users?.All}
                            itemLimit={5}
                            required={true}
                            errorMessage={errors.Owners}
                            selectedUsers={aiChat.App04AIChatUsers?.map(u => u.UserID)}
                            onChange={v => changeHandlers.changeMultiUserPicker(v, 'App04AIChatUsers', new App04AIChatUser(), 'UserID')}
                        />

                        <CrDropdown
                            label="Status"
                            className={styles.formField}
                            disabled={disableGroupManagement}
                            options={LookupService.entitiesToSelectableOptions(lookupData.EntityStatuses)}
                            selectedKey={aiChat.EntityStatusID}
                            onChange={(_, o) => changeHandlers.changeStatusDropdown(o, 'EntityStatusID')}
                        />

                    </div>
                );
            }}
            loadEntity={id => dataServices.app04AIChatService.read(id, true, true)}
            loadNewEntity={() => {
                const chat = new App04AIChat();
                chat.App04AIChatUsers = [
                    {
                        ...new App04AIChatUser(),
                        UserID: currentUserId,
                    },
                ];

                return chat;
            }}
            loadEntityValidations={() => new AiChatFormValidations()}
            onValidateEntity={validateEntity}
            onCreate={c => dataServices.app04AIChatService.create(c)}
            onUpdate={c => dataServices.app04AIChatService.update(c.ID, c)}
            parentEntities={dataServices.app04AIChatService.parentEntities}
            childEntities={[
                {
                    ObjectParentProperty: 'App04AIChatUsers',
                    ParentIdProperty: 'ChatID',
                    ChildIdProperty: 'UserID',
                    ChildService: dataServices.app04AIChatUserService,
                },
            ]}
        />
    );
};
